import { useState, useCallback, useRef, useEffect } from 'react';
import type {
  DualGameState,
  PlayerState,
  GameConfig,
  PlayerSide,
  Winner,
  Tile,
  GameEvent,
} from '../types/dualGame';
import {
  resetTileIds,
  createEmptyBoard,
  addRandomTile,
  moveBoard,
  canMove,
  type Direction,
} from '../utils/gameLogic';

const createInitialPlayerState = (side: PlayerSide, boardSize: number): PlayerState => {
  let board = createEmptyBoard(boardSize);
  const r1 = addRandomTile(board);
  board = r1.board;
  const r2 = addRandomTile(board);
  board = r2.board;
  const tiles = [r1.tile, r2.tile].filter(Boolean) as Tile[];
  return { side, board, tiles, score: 0, bestScore: 0, isDead: false, hasReachedTarget: false };
};

const createInitialState = (config: GameConfig): DualGameState => ({
  status: 'config',
  leftPlayer: createInitialPlayerState('left', config.boardSize),
  rightPlayer: createInitialPlayerState('right', config.boardSize),
  timeRemaining: config.duration,
  countdownValue: 0,
  winner: null,
  config,
});

const determineWinner = (left: PlayerState, right: PlayerState): Winner => {
  if (left.score > right.score) return 'left';
  if (left.score < right.score) return 'right';
  return 'draw';
};

export type GameEventCallback = (event: GameEvent) => void;

export const useDualGame = (onEvent?: GameEventCallback) => {
  const [state, setState] = useState<DualGameState>(() =>
    createInitialState({ duration: 180, targetScore: 8192, boardSize: 4 })
  );

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const configRef = useRef<GameConfig>({ duration: 180, targetScore: 8192, boardSize: 4 });
  const onEventRef = useRef(onEvent);

  useEffect(() => { onEventRef.current = onEvent; }, [onEvent]);

  const clearTimers = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setState(prev => {
        if (prev.status !== 'playing') return prev;
        const newTime = prev.timeRemaining - 1;

        if (newTime <= 10 && newTime > 0) {
          onEventRef.current?.({ type: 'TICK', remaining: newTime });
        }

        if (newTime <= 0) {
          if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
          const winner = determineWinner(prev.leftPlayer, prev.rightPlayer);
          onEventRef.current?.({ type: 'GAME_FINISHED', winner, reason: 'time' });
          return { ...prev, timeRemaining: 0, status: 'finished', winner };
        }
        return { ...prev, timeRemaining: newTime };
      });
    }, 1000);
  }, []);

  const startGame = useCallback((config: GameConfig) => {
    clearTimers();
    configRef.current = config;
    resetTileIds();

    const initial = createInitialState(config);
    initial.status = 'countdown';
    initial.countdownValue = 3;
    setState(initial);

    onEventRef.current?.({ type: 'COUNTDOWN', value: 3 });

    let count = 3;
    countdownRef.current = setInterval(() => {
      count--;
      if (count > 0) {
        setState(prev => ({ ...prev, countdownValue: count }));
        onEventRef.current?.({ type: 'COUNTDOWN', value: count });
      } else {
        if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
        setState(prev => ({ ...prev, status: 'playing', countdownValue: 0 }));
        startTimer();
      }
    }, 1000);
  }, [clearTimers, startTimer]);

  const pauseGame = useCallback(() => {
    setState(prev => {
      if (prev.status === 'playing') {
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        return { ...prev, status: 'paused' };
      }
      return prev;
    });
  }, []);

  const resumeGame = useCallback(() => {
    setState(prev => {
      if (prev.status === 'paused') {
        startTimer();
        return { ...prev, status: 'playing' };
      }
      return prev;
    });
  }, [startTimer]);

  const resetGame = useCallback(() => {
    clearTimers();
    resetTileIds();
    setState(createInitialState(configRef.current));
  }, [clearTimers]);

  const movePlayer = useCallback((player: PlayerSide, direction: Direction) => {
    setState(prev => {
      if (prev.status !== 'playing') return prev;

      const playerKey = player === 'left' ? 'leftPlayer' : 'rightPlayer';
      const ps = prev[playerKey];
      if (ps.isDead) return prev;

      const { newBoard, newTiles, scoreDelta, hasMoved, hasMerge } = moveBoard(ps.board, ps.tiles, direction);
      if (!hasMoved) return prev;

      const spawnResult = addRandomTile(newBoard);
      const finalBoard = spawnResult.board;
      const finalTiles = spawnResult.tile ? [...newTiles, spawnResult.tile] : newTiles;

      const isDead = !canMove(finalBoard);
      const newScore = ps.score + scoreDelta;
      const targetScore = configRef.current.targetScore;
      const hasReachedTarget = targetScore > 0 && newScore >= targetScore;

      const newPlayerState: PlayerState = {
        ...ps,
        board: finalBoard,
        tiles: finalTiles,
        score: newScore,
        bestScore: Math.max(ps.bestScore, newScore),
        isDead,
        hasReachedTarget,
      };

      onEventRef.current?.({ type: 'MOVE', player, scoreDelta, merged: hasMerge });

      if (hasReachedTarget && !ps.hasReachedTarget) {
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        onEventRef.current?.({ type: 'TARGET_REACHED', player });
        onEventRef.current?.({ type: 'GAME_FINISHED', winner: player, reason: 'target' });
        return { ...prev, [playerKey]: newPlayerState, status: 'finished' as const, winner: player };
      }

      if (isDead && !ps.isDead) {
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        const winner: PlayerSide = player === 'left' ? 'right' : 'left';
        onEventRef.current?.({ type: 'PLAYER_DEAD', player });
        onEventRef.current?.({ type: 'GAME_FINISHED', winner, reason: 'death' });
        return { ...prev, [playerKey]: newPlayerState, status: 'finished' as const, winner };
      }

      return { ...prev, [playerKey]: newPlayerState };
    });
  }, []);

  return {
    state,
    startGame,
    pauseGame,
    resumeGame,
    resetGame,
    movePlayer,
    leftPlayer: state.leftPlayer,
    rightPlayer: state.rightPlayer,
    status: state.status,
    timeRemaining: state.timeRemaining,
    countdownValue: state.countdownValue,
    winner: state.winner,
    config: state.config,
  };
};

export default useDualGame;
